import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import instance from "services/axiosServices";
import { buildWhatsAppSendFormData, WhatsAppSendPayload } from "./sendFormData";

const BASE_PATH = "whatsapp/common/whatsapp";

/**
 * GET /whatsapp/common/whatsapp/status/v1
 * Retorna: { success: true, data: { conectado: boolean, status: string }, ... }
 */
export function useWhatsAppStatus() {
  return useQuery({
    queryKey: ["whatsapp-status"],
    queryFn: async () => {
      const response = await instance.get(`${BASE_PATH}/status/v1`);
      return response.data;
    },
    refetchOnWindowFocus: false,
    refetchInterval: 30000,
    retry: false,
    onError: (error: any) => {
      const notifications = error?.response?.data?.notifications;
      const errorMessages = Array.isArray(notifications)
        ? notifications.map((item: any) => item?.message).join("\n")
        : "Erro ao verificar status do WhatsApp";
      toast.error(errorMessages, { autoClose: 10000 });
    },
  });
}

/**
 * POST /whatsapp/common/whatsapp/session/v1
 * Retorna: { success: true, data: { qrCode: string, sessionId: string }, ... }
 */
export function useWhatsAppSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await instance.post(`${BASE_PATH}/session/v1`);
      return response.data;
    },
    mutationKey: ["whatsapp-session"],
    onSuccess: () => {
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["whatsapp-status"] });
      }, 5000);
    },
    onError: (error: any) => {
      const notifications = error?.response?.data?.notifications;
      const errorMessages = Array.isArray(notifications)
        ? notifications.map((item: any) => item?.message).join("\n")
        : "Erro ao criar sessão do WhatsApp";
      toast.error(errorMessages, { autoClose: 10000 });
    },
  });
}

/**
 * DELETE /whatsapp/common/whatsapp/session/v1
 * Encerra a sessão do WhatsApp
 */
export function useWhatsAppDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await instance.delete(`${BASE_PATH}/session/v1`);
      return response.data;
    },
    mutationKey: ["whatsapp-disconnect"],
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["whatsapp-status"] });
      toast.success("Sessão do WhatsApp encerrada.");
    },
    onError: (error: any) => {
      const notifications = error?.response?.data?.notifications;
      const errorMessages = Array.isArray(notifications)
        ? notifications.map((item: any) => item?.message).join("\n")
        : "Erro ao desconectar WhatsApp";
      toast.error(errorMessages, { autoClose: 10000 });
    },
  });
}

/**
 * POST /whatsapp/common/whatsapp/send/v1
 * Body: { idLeadCorretor365: string, mensagem: string }
 */
export function useWhatsAppSend() {
  return useMutation({
    mutationFn: async (body: WhatsAppSendPayload) => {
      const formData = buildWhatsAppSendFormData(body);
      const response = await instance.post(`${BASE_PATH}/send/v1`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    mutationKey: ["whatsapp-send"],
    onError: (error: any) => {
      const notifications = error?.response?.data?.notifications;
      const errorMessages = Array.isArray(notifications)
        ? notifications.map((item: any) => item?.message).join("\n")
        : "Erro ao enviar mensagem via WhatsApp";
      toast.error(errorMessages, { autoClose: 10000 });
    },
  });
}
